import React, { useState, useRef } from 'react';
import { WikipediaArticle } from '../types';
import { Heart, Share2, Info, X, ExternalLink, ThumbsDown } from 'lucide-react';

interface ArticleCardProps {
  article: WikipediaArticle & { aiSummary?: string };
  isLiked?: boolean;
  onToggleLike?: () => void;
  onDismiss?: () => void;
  onFullArticleChange?: (isOpen: boolean) => void;
  onModalTime?: (durationMs: number) => void;
}

const CATEGORY_KEYWORDS: Record<string, { keywords: string[], label: string, color: string }> = {
  science: { keywords: ['experiment', 'discovery', 'quantum', 'physics', 'chemistry', 'biology', 'atom', 'molecule', 'laboratory'], label: 'Science', color: 'bg-teal-500/20 text-teal-300 border-teal-500/30' },
  space: { keywords: ['nasa', 'astronaut', 'planet', 'galaxy', 'orbit', 'rocket', 'spacecraft', 'mars', 'moon', 'star', 'telescope'], label: 'Space', color: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' },
  history: { keywords: ['ancient', 'empire', 'medieval', 'century', 'kingdom', 'dynasty', 'pharaoh', 'viking', 'roman'], label: 'History', color: 'bg-amber-600/20 text-amber-300 border-amber-600/30' },
  animals: { keywords: ['species', 'animal', 'mammal', 'bird', 'predator', 'wildlife', 'dinosaur', 'shark', 'whale'], label: 'Animals', color: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
  food: { keywords: ['cuisine', 'chef', 'restaurant', 'dish', 'cooking', 'recipe', 'food', 'flavor', 'ingredient'], label: 'Food', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
  music: { keywords: ['album', 'band', 'concert', 'songwriter', 'singer', 'musician', 'genre', 'guitar', 'record label'], label: 'Music', color: 'bg-pink-500/20 text-pink-300 border-pink-500/30' },
  movies_tv: { keywords: ['film', 'movie', 'director', 'oscar', 'series', 'television', 'actress', 'actor', 'box office'], label: 'Movies & TV', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  gaming: { keywords: ['video game', 'esports', 'nintendo', 'playstation', 'xbox', 'console', 'multiplayer', 'arcade'], label: 'Gaming', color: 'bg-green-500/20 text-green-300 border-green-500/30' },
  art: { keywords: ['painting', 'sculptor', 'gallery', 'museum', 'masterpiece', 'exhibition', 'canvas', 'portrait'], label: 'Art', color: 'bg-rose-500/20 text-rose-300 border-rose-500/30' },
  psychology: { keywords: ['cognitive', 'personality', 'behavior', 'perception', 'emotion', 'psychology', 'mental'], label: 'Psychology', color: 'bg-violet-500/20 text-violet-300 border-violet-500/30' },
  medicine: { keywords: ['vaccine', 'disease', 'surgery', 'pandemic', 'hospital', 'treatment', 'diagnosis', 'symptom'], label: 'Medicine', color: 'bg-red-500/20 text-red-300 border-red-500/30' },
  geography: { keywords: ['volcano', 'earthquake', 'mountain', 'canyon', 'glacier', 'desert', 'island', 'river'], label: 'Geography', color: 'bg-lime-500/20 text-lime-300 border-lime-500/30' },
  mythology: { keywords: ['mythology', 'legend', 'myth', 'folklore', 'god', 'hero', 'mythical', 'dragon'], label: 'Mythology', color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  cars: { keywords: ['supercar', 'motorsport', 'racing', 'engine', 'horsepower', 'formula', 'automobile'], label: 'Cars', color: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
  military: { keywords: ['military', 'army', 'battle', 'special forces', 'tank', 'navy', 'warfare', 'soldier'], label: 'Military', color: 'bg-stone-500/20 text-stone-300 border-stone-500/30' },
  ocean: { keywords: ['ocean', 'marine', 'shipwreck', 'submarine', 'coral', 'deep sea', 'underwater'], label: 'Ocean', color: 'bg-cyan-600/20 text-cyan-300 border-cyan-600/30' },
  business: { keywords: ['billionaire', 'ceo', 'company', 'startup', 'revenue', 'investment', 'entrepreneur'], label: 'Business', color: 'bg-green-500/20 text-green-300 border-green-500/30' },
  football: { keywords: ['football', 'soccer', 'premier league', 'fifa', 'goal', 'striker', 'midfielder'], label: 'Football', color: 'bg-green-600/20 text-green-300 border-green-600/30' },
  sports: { keywords: ['championship', 'athlete', 'tournament', 'medal', 'olympic', 'boxing', 'basketball'], label: 'Sports', color: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
  tech: { keywords: ['technology', 'smartphone', 'software', 'hardware', 'programming', 'digital', 'computing'], label: 'Tech', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  climate: { keywords: ['climate', 'environment', 'renewable', 'sustainable', 'emission', 'conservation'], label: 'Climate', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  drama: { keywords: ['murder', 'scandal', 'crime', 'heist', 'serial killer', 'prison', 'trial'], label: 'True Crime', color: 'bg-red-600/20 text-red-300 border-red-600/30' },
};

const detectCategory = (article: WikipediaArticle): { label: string, color: string } | null => {
  const text = `${article.title} ${article.description || ''} ${article.extract || ''}`.toLowerCase();
  for (const cat of Object.values(CATEGORY_KEYWORDS)) {
    if (cat.keywords.some(kw => text.includes(kw))) {
      return { label: cat.label, color: cat.color };
    }
  }
  return null;
};

const estimateReadingTime = (text: string): number => {
  const words = text.split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
};

export const ArticleCard = React.memo<ArticleCardProps>(({ article, isLiked, onToggleLike, onDismiss, onFullArticleChange, onModalTime }) => {
  const [showFullArticle, setShowFullArticle] = useState(false);
  const [fullContent, setFullContent] = useState<string>('');
  const [loadingFull, setLoadingFull] = useState(false);
  const [heartAnimating, setHeartAnimating] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showDoubleTapHeart, setShowDoubleTapHeart] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const modalOpenTime = useRef<number>(0);
  const lastTapTime = useRef<number>(0);

  const imageUrl = article.thumbnail?.source;
  const category = detectCategory(article);
  const readTime = estimateReadingTime(article.extract || '');

  const getFallbackText = (text: string) => {
    if (!text) return "";
    const sentences = text.trim().split(/\. (?=[A-ZÆØÅ])/);
    if (sentences.length > 1) {
      const result = sentences.slice(0, 2).join('. ').trim();
      return result.endsWith('.') ? result : result + '.';
    }
    return text.endsWith('.') ? text : text + '.';
  };

  const fetchFullArticle = async () => {
    setLoadingFull(true);
    try {
      const title = article.title;
      const apiUrl = 'https://en.wikipedia.org/w/api.php';

      const params = new URLSearchParams({
        action: 'query',
        titles: title,
        prop: 'extracts',
        explaintext: '1',
        format: 'json',
        origin: '*'
      });

      const response = await fetch(`${apiUrl}?${params.toString()}`);
      const data = await response.json();

      const pages = data.query?.pages;
      if (pages) {
        const page = Object.values(pages)[0] as any;
        setFullContent(page.extract || 'Content not available.');
      }
    } catch (error) {
      setFullContent('Failed to load full article.');
    } finally {
      setLoadingFull(false);
    }
  };

  const handleReadMore = () => {
    setShowFullArticle(true);
    modalOpenTime.current = Date.now();
    onFullArticleChange?.(true);
    if (!fullContent) {
      fetchFullArticle();
    }
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLiked) {
      setHeartAnimating(true);
      setTimeout(() => setHeartAnimating(false), 600);
    }
    onToggleLike?.();
  };

  const handleDoubleTap = (e: React.MouseEvent | React.TouchEvent) => {
    const now = Date.now();
    if (now - lastTapTime.current < 300) {
      // Double tap detected
      e.preventDefault();
      if (!isLiked) {
        onToggleLike?.();
      }
      setShowDoubleTapHeart(true);
      setHeartAnimating(true);
      try { navigator.vibrate?.(10); } catch (_) {}
      setTimeout(() => {
        setShowDoubleTapHeart(false);
        setHeartAnimating(false);
      }, 800);
      lastTapTime.current = 0;
    } else {
      lastTapTime.current = now;
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDismissing(true);
    try { navigator.vibrate?.(10); } catch (_) {}
    setTimeout(() => {
      onDismiss?.();
    }, 300);
  };

  return (
    <>
      <div
        className="snap-card relative w-full h-[100dvh] snap-start flex flex-col bg-black overflow-hidden border-b border-white/5"
        style={dismissing ? { animation: 'dismissSlide 0.3s ease-out forwards' } : undefined}
      >
        {/* Image Section (Top - 50%) — double-tap zone */}
        <div
          className="relative w-full h-[50vh] bg-[#050505] flex items-center justify-center overflow-hidden"
          onClick={handleDoubleTap}
        >
          {imageUrl ? (
            <>
              <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-white/[0.04] via-transparent to-white/[0.02]" />
              {!imageLoaded && (
                <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-white/[0.06] to-white/[0.02] z-[5]" />
              )}
              <img
                src={imageUrl}
                alt={article.title}
                className="relative z-10 w-full h-full object-contain p-6"
                onLoad={() => setImageLoaded(true)}
              />
              <div className="absolute inset-0 z-20 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
            </>
          ) : (
            <div className="relative flex flex-col items-center justify-center w-full h-full bg-[#0a0a0a]">
               <div className="font-black text-[14rem] text-white/[0.03] select-none absolute">W</div>
               <div className="z-10 w-16 h-16 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center text-white/20">
                 <Info size={32} />
               </div>
            </div>
          )}

          {/* Double-tap heart animation */}
          {showDoubleTapHeart && (
            <div
              className="absolute z-50 pointer-events-none"
              style={{
                top: '50%',
                left: '50%',
                animation: 'doubleTapHeart 0.8s ease-out forwards',
              }}
            >
              <Heart size={80} fill="white" stroke="white" strokeWidth={1.5} className="drop-shadow-2xl" />
            </div>
          )}

          {/* Action Buttons */}
          <div className="absolute right-5 bottom-8 z-40 flex flex-col items-center gap-5">
            <button
              onClick={handleLike}
              className={`w-14 h-14 rounded-full border shadow-2xl flex items-center justify-center transition-all active:scale-90 ${
                isLiked
                  ? 'bg-red-500 border-red-500 text-white'
                  : 'bg-black/60 backdrop-blur-md border-white/20 text-white'
              }`}
              style={heartAnimating ? { animation: 'heartPop 0.6s ease-out' } : undefined}
            >
              <Heart size={28} fill={isLiked ? "currentColor" : "none"} strokeWidth={2.5} />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                const shareUrl = `https://scrolling.no/?a=${encodeURIComponent(article.title)}`;
                if (navigator.share) {
                  navigator.share({ title: article.title, url: shareUrl });
                } else {
                  navigator.clipboard?.writeText(shareUrl);
                }
              }}
              className="w-14 h-14 rounded-full bg-black/60 backdrop-blur-md border border-white/20 flex items-center justify-center text-white shadow-2xl active:scale-90 transition-all"
            >
              <Share2 size={24} strokeWidth={2.5} />
            </button>

            {onDismiss && (
              <button
                onClick={handleDismiss}
                className="w-14 h-14 rounded-full bg-black/60 backdrop-blur-md border border-white/20 flex items-center justify-center text-white/50 shadow-2xl active:scale-90 transition-all"
              >
                <ThumbsDown size={22} strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>

        {/* Text Section (Bottom - 50%) */}
        <div className="flex-1 bg-[#080808] p-6 pt-5 flex flex-col relative overflow-hidden border-t border-white/5">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          <div className="max-w-xl mx-auto w-full flex flex-col h-full">
            <div className="mb-2 flex items-center gap-2">
              {category && (
                <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${category.color}`}>
                  {category.label}
                </span>
              )}
              {article.description && (
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/30 truncate">
                  {article.description}
                </span>
              )}
            </div>

            <h2 className="text-2xl font-black mb-4 leading-[1.15] tracking-tight text-white">
              {article.title}
            </h2>

            <div className="text-[15px] text-white/65 leading-[1.6] font-normal overflow-hidden pb-3 flex-1">
              <p className="line-clamp-6">
                {article.aiSummary || getFallbackText(article.extract)}
              </p>
            </div>

            {/* Read More Button */}
            <div className="mt-auto pt-3 border-t border-white/[0.05] flex items-center justify-between">
               <div className="flex items-center gap-3">
                 <button
                   onClick={handleReadMore}
                   className="text-[11px] font-black uppercase tracking-[0.3em] text-white/40 hover:text-white/70 transition-colors"
                 >
                   Read Full Article
                 </button>
                 <span className="text-[10px] text-white/20">{readTime} min read</span>
               </div>
               <a
                href={article.content_urls.mobile.page}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/20 hover:text-white/40 transition-colors"
                onClick={(e) => e.stopPropagation()}
               >
                 <ExternalLink size={16} />
               </a>
            </div>
          </div>
        </div>
      </div>

      {/* Full Article Overlay */}
      {showFullArticle && (
        <div className="fixed inset-0 z-[200] bg-black flex flex-col">
          {/* Article Content - Scrollable */}
          <div className="flex-1 overflow-y-auto bg-gradient-to-b from-[#080808] to-black pt-4">
            <div className="max-w-3xl mx-auto px-4 py-6 pb-20">
              {/* Compact Image Card for Mobile */}
              {imageUrl && (
                <div className="mb-6 rounded-2xl overflow-hidden bg-gradient-to-br from-white/[0.07] to-white/[0.02] p-1 shadow-xl border border-white/5">
                  <div className="rounded-xl overflow-hidden bg-black">
                    <img
                      src={imageUrl}
                      alt={article.title}
                      className="w-full h-48 object-cover"
                    />
                  </div>
                </div>
              )}

              {/* Title Card - More Compact */}
              <div className="mb-5 p-5 rounded-2xl bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/5 shadow-xl">
                {category && (
                  <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border mb-3 ${category.color}`}>
                    {category.label}
                  </span>
                )}
                <h1 className="text-3xl font-black text-white leading-[1.1] mb-2">
                  {article.title}
                </h1>
                {article.description && (
                  <p className="text-sm text-white/50 font-medium leading-relaxed">
                    {article.description}
                  </p>
                )}
              </div>

              {/* Full Content */}
              {loadingFull ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-2 border-white/10 border-t-white rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-3">
                  {fullContent.split('\n\n').map((section, idx) => {
                    const trimmed = section.trim();
                    if (!trimmed) return null;

                    const headingMatch = trimmed.match(/^=+\s*(.+?)\s*=+$/);

                    if (headingMatch) {
                      const headingText = headingMatch[1].trim();

                      const skipSections = ['see also', 'references', 'external links', 'notes', 'bibliography', 'further reading'];
                      if (skipSections.some(skip => headingText.toLowerCase() === skip)) {
                        return null;
                      }

                      return (
                        <div key={idx} className="mt-6 mb-3">
                          <h2 className="text-xl font-black text-white/90 tracking-tight">
                            {headingText}
                          </h2>
                          <div className="mt-2 h-0.5 w-12 bg-gradient-to-r from-white/30 to-transparent rounded-full" />
                        </div>
                      );
                    }

                    if (trimmed.startsWith('*') || trimmed.match(/^\d+\.\s*\^/)) {
                      return null;
                    }

                    return (
                      <div
                        key={idx}
                        className="p-4 rounded-xl bg-white/[0.03] border border-white/5"
                      >
                        <p className="text-[15px] text-white/80 leading-[1.65] font-normal">
                          {trimmed}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Bottom Spacer */}
              <div className="h-8" />

              {/* Wikipedia Link at Bottom */}
              <div className="flex items-center justify-center pb-6">
                <a
                  href={article.content_urls.mobile.page}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-white/10 text-white/50 hover:bg-white/10 hover:text-white/70 transition-colors text-sm font-bold"
                >
                  <ExternalLink size={16} />
                  <span>View on Wikipedia</span>
                </a>
              </div>
            </div>
          </div>

          {/* Sticky Exit Button - MUST BE LAST TO BE ON TOP */}
          <button
            onClick={() => {
              setShowFullArticle(false);
              if (modalOpenTime.current > 0) {
                onModalTime?.(Date.now() - modalOpenTime.current);
                modalOpenTime.current = 0;
              }
              onFullArticleChange?.(false);
            }}
            className="fixed top-6 right-6 z-[250] w-14 h-14 flex items-center justify-center rounded-full bg-black border-2 border-white/30 text-white shadow-2xl hover:bg-white/10 active:scale-95 transition-all"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X size={24} strokeWidth={3} />
          </button>
        </div>
      )}
    </>
  );
});

ArticleCard.displayName = 'ArticleCard';
